// src/hooks/sync/useAutoChallengeProgress.js
// ─────────────────────────────────────────────────────────────────────────────
//  Automatic Challenge Progress Tracking
// ─────────────────────────────────────────────────────────────────────────────
//
//  This hook monitors store data and automatically updates challenge progress
//  based on the autoProgress configuration in each challenge template.
//
//  When data is stored (e.g., a sermon note is saved), challenges with
//  matching autoProgress configurations will automatically receive a check-in
//  for that day — no manual action required.
//
//  If all related data is deleted, the challenge progress will revert to zero
//  (no check-ins for days without data).
//
//  USAGE:
//    const { syncChallengeProgress } = useAutoChallengeProgress();
//    // Call syncChallengeProgress() when data changes in any monitored store
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect } from 'react';
import { useSyncStore } from './useSyncStore';
import { useChallengeRequirements } from './useChallengeRequirements';
import {
  STORE_CHALLENGES,
  STORE_SERMON_NOTES,
  STORE_BIBLE_READING_HISTORY,
  STORE_DAILY_ENTRIES,
  STORE_PRAYER_CHECKINS,
  STORE_EXPENSES,
  STORE_DISCIPLESHIP_DATA,
  STORE_PEOPLE_CONTACTS,
  // ── Added: stores previously missing from this hook ──────────────
  STORE_CALENDAR_EVENTS,          // calendarEvents storeKey
  STORE_BIBLE_STUDY_PROGRESS,     // bibleStudyProgress storeKey
  STORE_DISCIPLESHIP_ATTENDANCE,  // discipleshipAttendance storeKey
  STORE_COMPLETED_CHALLENGES,
  STORE_TITHES_OFFERING,
  STORE_PRAYER_REQUESTS,
  STORE_MEMORY_VERSE_DAYS,
} from '@/lib/storeKeys';
import { formatDate } from '@/utils/helpers';
import { resolveAutoProgress, isConsecutiveChallenge } from '@/data/challengeLibrary';
import { useFeatureUnlock } from '@/hooks/feature/useFeatureUnlock';

// Get today's date in local ISO format
const todayISO = () => formatDate(new Date());

// Shift a YYYY-MM-DD local day by N days (negative = earlier).
function shiftDay(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  return formatDate(new Date(y, m - 1, d + delta));
}

// Did the user write anything in this day's journal entry? Mirrors the
// dailyEntries branch of getTodayCount below.
function entryHasContent(e) {
  return !!(e && (e.hasEdits || e.practiceNotes?.trim() || e.praises?.trim() || e.prayer?.trim() ||
    e.passage?.trim() || e.principle?.trim() || e.practice?.trim() || e.praise?.trim()));
}

// Consecutive-day challenges: the check-ins are the current unbroken run of
// journal days (oldest first), counted only from startDate. A run counts while
// its latest day is today or yesterday; a missed day with no entry restarts the
// count, and writing into the missed past day repairs it.
export function consecutiveRun(dailyEntries, startDate, today, maxDays) {
  if (!dailyEntries || typeof dailyEntries !== 'object' || !startDate) return [];
  const filled = (day) => day >= startDate && day <= today && entryHasContent(dailyEntries[day]);
  const end = filled(today) ? today : (filled(shiftDay(today, -1)) ? shiftDay(today, -1) : null);
  if (!end) return [];
  const run = [];
  for (let day = end; filled(day); day = shiftDay(day, -1)) run.unshift(day);
  return maxDays > 0 ? run.slice(0, maxDays) : run;
}

// Local calendar day (YYYY-MM-DD) of an ISO timestamp. createdAt values come from
// toISOString() (UTC), so slicing them directly is a day off for UTC+8 users
// before 8 AM local time.
function localDay(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : formatDate(d);
}

// Does a stored item belong to the given local day?
function itemMatchesDay(item, day, storeKey) {
  if (typeof item === 'string') return item.slice(0, 10) === day;
  if (!item || typeof item !== 'object') return false;
  // calendarEvents: only events the user created that day count. Events that
  // handlers.js auto-generates (sourceType set) or that are merely dated today
  // are not "calendar setup" activity.
  if (storeKey === 'calendarEvents') {
    return !item.sourceType && !!item.createdAt && localDay(item.createdAt) === day;
  }
  if (item.date && String(item.date).slice(0, 10) === day) return true;
  return !!item.createdAt && localDay(item.createdAt) === day;
}

/**
 * Get the count of items created/updated today from store data
 */
function getTodayCount(storeData, challenge, prayerRequests) {
  const config = challenge.autoProgress;
  if (!storeData) return 0;

  // Handle array stores (sermonNotes, bibleReadingHistory, etc.)
  if (Array.isArray(storeData)) {
    const today = todayISO();
    let todayItems = storeData.filter(item => {
      // Check if item has a date field that matches today
      if (itemMatchesDay(item, today, config.storeKey)) {
        return true;
      }
      // For reading history, items are date strings
      if (typeof item === 'string' && item === today) {
        return true;
      }
      return false;
    });

    // filterCategory — e.g. tithe_consistency uses expenses store but should
    // only count items where item.category === config.filterCategory ('Tithe').
    // No-op when filterCategory is absent (all other array-based challenges).
    if (config.filterCategory) {
      todayItems = todayItems.filter(item => item.category === config.filterCategory);
    }

    // If countField is specified (e.g. bibleStudyProgress → 'completed'),
    // further filter to items where that field is truthy.
    if (config.countField) {
      return todayItems.filter(item => item[config.countField]).length;
    }

    return todayItems.length;
  }

  // Handle object stores (dailyEntries, prayerCheckins, etc.)
  if (typeof storeData === 'object') {
    const today = todayISO();
    // dailyEntries: only count real journal activity, not auto-written
    // fields such as tasks / customTasks / updatedAt.
    if (challenge.autoProgress?.storeKey === 'dailyEntries') {
      const e = storeData[today];
      return e && (e.hasEdits || e.practiceNotes?.trim() || e.praises?.trim() || e.prayer?.trim() ||
        e.passage?.trim() || e.principle?.trim() || e.practice?.trim() || e.praise?.trim()) ? 1 : 0;
    }
    // Prayer check-ins are keyed by prayer-request id. When the challenge names a
    // request category (e.g. disciple prayer -> 'timothy'), only requests of that
    // category count.
    if (config.requestCategory) {
      const dayMap = storeData[today] || {};
      const reqs = Array.isArray(prayerRequests) ? prayerRequests : [];
      return Object.keys(dayMap).some(id =>
        dayMap[id] && reqs.some(r => String(r.id) === id && r.category === config.requestCategory)
      ) ? 1 : 0;
    }
    // Date-ledger objects, e.g. bibleReadingHistory: { 'John 3': 'YYYY-MM-DD' }
    if (Object.values(storeData).some(v => v === today)) {
      return 1;
    }
    if (storeData[today]) {
      const value = storeData[today];
      if (typeof value === 'object' && value !== null) {
        // Count non-empty fields or specific threshold
        if (config.threshold) {
          return Object.keys(value).filter(k => value[k]).length >= config.threshold ? 1 : 0;
        }
        return Object.keys(value).length > 0 ? 1 : 0;
      }
      if (typeof value === 'number') {
        return value >= (config.threshold || 1) ? 1 : 0;
      }
      return value ? 1 : 0;
    }
  }

  return 0;
}

/**
 * Check if a challenge already has a check-in for today
 */
function hasTodayCheckIn(challenge) {
  const today = todayISO();
  return (challenge.checkIns || []).includes(today);
}

/**
 * Main hook for automatic challenge progress tracking
 */
export function useAutoChallengeProgress() {
  const [challenges, setChallenges, challengesReady] = useSyncStore(STORE_CHALLENGES, []);
  const [completedList, setCompleted, completedReady] = useSyncStore(STORE_COMPLETED_CHALLENGES, []);
  const { unmetRequirement, ready: requirementsReady } = useChallengeRequirements();
  const { effectiveLevel, levelTasks, ready: levelReady } = useFeatureUnlock();

  // Subscribe to all stores that can trigger auto-progress
  const [sermonNotes, , sermonNotesReady]           = useSyncStore(STORE_SERMON_NOTES,            []);
  const [bibleReadingHistory, , bibleReadingHistoryReady]   = useSyncStore(STORE_BIBLE_READING_HISTORY,   []);
  const [dailyEntries, , dailyEntriesReady]          = useSyncStore(STORE_DAILY_ENTRIES,           {});
  const [prayerCheckins, , prayerCheckinsReady]        = useSyncStore(STORE_PRAYER_CHECKINS,         {});
  const [expenses, , expensesReady]              = useSyncStore(STORE_EXPENSES,                []);
  const [discipleshipData, , discipleshipDataReady]      = useSyncStore(STORE_DISCIPLESHIP_DATA,       []);
  const [peopleContacts, , peopleContactsReady]        = useSyncStore(STORE_PEOPLE_CONTACTS,         []);
  // ── Added subscriptions ───────────────────────────────────────────────────
  const [calendarEvents, , calendarEventsReady]        = useSyncStore(STORE_CALENDAR_EVENTS,         []);
  const [bibleStudyProgress, , bibleStudyProgressReady]    = useSyncStore(STORE_BIBLE_STUDY_PROGRESS,    {});
  const [discipleshipAttendance, , discipleshipAttendanceReady]= useSyncStore(STORE_DISCIPLESHIP_ATTENDANCE, []);
  const [tithesOffering, , tithesOfferingReady]                 = useSyncStore(STORE_TITHES_OFFERING,         []);
  const [prayerRequests, , prayerRequestsReady]                 = useSyncStore(STORE_PRAYER_REQUESTS,         []);
  const [memoryVerseDays, , memoryVerseDaysReady] = useSyncStore(STORE_MEMORY_VERSE_DAYS, []);

  // Wait until every monitored store has finished its async load. Before that they
// hold empty defaults, and the cleanup pass would strip valid check-ins.
  const allReady = sermonNotesReady && bibleReadingHistoryReady && dailyEntriesReady &&
    prayerCheckinsReady && expensesReady && discipleshipDataReady && peopleContactsReady &&
    calendarEventsReady && bibleStudyProgressReady && discipleshipAttendanceReady && tithesOfferingReady && prayerRequestsReady && memoryVerseDaysReady;

  // Store data lookup — key must match challenge.autoProgress.storeKey exactly
  const storeData = {
    sermonNotes,
    bibleReadingHistory,
    dailyEntries,
    prayerCheckins,
    expenses,
    tithesOffering,     // tithe_consistency
    memoryVerseDays,
    discipleshipData,
    peopleContacts,
    // ── Added ─────────────────────────────────────────────────────────────
    calendarEvents,
    bibleStudyProgress,
    discipleshipAttendance,
  };

  /**
   * Sync challenge progress based on current store data
   * This should be called whenever any monitored store changes
   */
  const syncChallengeProgress = useCallback(() => {
    if (!allReady) return;
    setChallenges(prev => {
      // Resolve each challenge's real-data tracker from its library template so
      // challenges started without an `autoProgress` field are still tracked.
      const currentChallenges = (Array.isArray(prev) ? prev : []).map(c => {
        if (c.autoProgress) return c;
        const ap = resolveAutoProgress(c);
        return ap ? { ...c, autoProgress: ap } : c;
      });
      let changed = false;
      const today = todayISO();

      const updated = currentChallenges.map(challenge => {
        // Consecutive-day challenges (e.g. 14-Day Daily Devotional): progress is the
        // current unbroken run of journal days, recomputed from the data each sync.
        if (isConsecutiveChallenge(challenge)) {
          const run = consecutiveRun(storeData.dailyEntries, '0000-00-00', today, challenge.days);
          const prevCheckIns = challenge.checkIns || [];
          if (run.length === prevCheckIns.length && run.every((d, i) => d === prevCheckIns[i])) return challenge;
          changed = true;
          return { ...challenge, checkIns: run, updatedAt: new Date().toISOString() };
        }
        // Skip if already completed or has today's check-in
        if (hasTodayCheckIn(challenge)) {
          return challenge;
        }

        const daysDone = (challenge.checkIns || []).length;
        if (daysDone >= (challenge.days || 0)) {
          return challenge;
        }

        // Check if this challenge has autoProgress config
        if (!challenge.autoProgress?.storeKey) {
          return challenge;
        }

        // Get the store data for this challenge
        const data = storeData[challenge.autoProgress.storeKey];
        if (!data) {
          return challenge;
        }

        // Get today's count
        const todayCount = getTodayCount(data, challenge, prayerRequests);
        const threshold = challenge.autoProgress.threshold || 1;

        // If threshold is met, add today's check-in
        if (todayCount >= threshold) {
          changed = true;
          return {
            ...challenge,
            checkIns: [...(challenge.checkIns || []), today],
            updatedAt: new Date().toISOString(),
          };
        }

        return challenge;
      });

      // Also handle reverting progress when data is deleted
      // If a challenge has check-ins but the source data no longer supports them,
      // we should remove those check-ins
      const cleaned = updated.map(challenge => {
        if (!challenge.autoProgress?.storeKey) return challenge;

        const data = storeData[challenge.autoProgress.storeKey];
        if (!data) return challenge;

        // For array-based stores, verify each check-in day has corresponding data
        if (Array.isArray(data) && challenge.checkIns?.length > 0 && challenge.autoProgress.storeKey !== 'peopleContacts') {
          const validCheckIns = challenge.checkIns.filter(checkInDate => {
            return data.some(item => {
              const matches = itemMatchesDay(item, checkInDate, challenge.autoProgress.storeKey);
              return matches;
            });
          });

          if (validCheckIns.length !== challenge.checkIns.length) {
            changed = true;
            return { ...challenge, checkIns: validCheckIns };
          }
        }

        return challenge;
      });

      return changed ? cleaned : prev;
    });
  }, [
    setChallenges,
    allReady,
    sermonNotes,
    bibleReadingHistory,
    dailyEntries,
    prayerCheckins,
    expenses,
    discipleshipData,
    peopleContacts,
    // ── Added ─────────────────────────────────────────────────────────────
    calendarEvents,
    bibleStudyProgress,
    discipleshipAttendance,
    tithesOffering,
    prayerRequests,
    memoryVerseDays,
    challenges,
  ]);

  /**
   * Auto-sync when any monitored store changes
   */
  useEffect(() => {
    syncChallengeProgress();
  }, [syncChallengeProgress]);

  /**
   * Manually trigger a sync (useful after bulk data operations)
   */
  const forceSync = useCallback(() => {
    syncChallengeProgress();
  }, [syncChallengeProgress]);

  /**
   * Get progress info for a specific challenge
   */
  const getChallengeProgress = useCallback((challenge) => {
    if (!challenge?.autoProgress?.storeKey) {
      return { autoTracked: false, progress: 0, total: challenge?.days || 0 };
    }

    const data = storeData[challenge.autoProgress.storeKey];
    const daysDone = (challenge.checkIns || []).length;
    const totalDays = challenge.days || 0;
    const progress = Math.min(Math.round((daysDone / totalDays) * 100), 100);

    return {
      autoTracked: true,
      progress,
      daysDone,
      totalDays,
      storeKey: challenge.autoProgress.storeKey,
    };
  }, []);

  /**
   * Auto-complete: once a library challenge reaches its day target it moves to
   * completed challenges immediately, so level progress no longer waits for a
   * manual "Mark Complete" tap. Custom challenges (no templateId) stay manual.
   */
  useEffect(() => {
    if (!allReady || !completedReady || !requirementsReady) return;
    const list = Array.isArray(challenges) ? challenges : [];
    const finished = list.filter(c =>
      c.templateId && (c.days || 0) > 0 && (c.checkIns || []).length >= c.days &&
      !unmetRequirement(c.templateId)
    );
    if (finished.length === 0) return;
    const nowIso = new Date().toISOString();
    const finishedIds = new Set(finished.map(c => c.id));
    // Order matters: write completed first (idempotent via doneIds), then drop from active.
    setCompleted(prev => {
      const done = Array.isArray(prev) ? prev : [];
      const doneIds = new Set(done.map(c => c.id));
      const fresh = finished
        .filter(c => !doneIds.has(c.id))
        .map(c => ({ ...c, completedAt: nowIso, updatedAt: nowIso }));
      return fresh.length > 0 ? [...fresh, ...done] : done;
    });
    setChallenges(prev => (Array.isArray(prev) ? prev : []).filter(c => !finishedIds.has(c.id)));
  }, [allReady, completedReady, requirementsReady, unmetRequirement, challenges, setChallenges, setCompleted]);

  // Auto-start: every challenge required for the user's current level begins on
  // its own. Waits for every store to finish loading (an unloaded, empty list
  // must not cause duplicates), skips challenges already active or completed,
  // and does nothing at Level 5 (rotating challenges are chosen by the user).
  useEffect(() => {
    if (!allReady || !completedReady || !challengesReady || !levelReady) return;
    if (effectiveLevel >= 5) return;
    const doneIds = new Set((Array.isArray(completedList) ? completedList : []).map(c => c.templateId));
    const missing = (Array.isArray(levelTasks) ? levelTasks : [])
      .filter(t => t?.challengeTemplate && !t.done && !t.active && !doneIds.has(t.challengeTemplate.id))
      .map(t => t.challengeTemplate);
    if (missing.length === 0) return;
    setChallenges(prev => {
      const list = Array.isArray(prev) ? prev : [];
      const have = new Set(list.map(c => c.templateId));
      const toAdd = missing.filter(tpl => !have.has(tpl.id));
      if (toAdd.length === 0) return prev;
      const nowIso = new Date().toISOString();
      const started = toAdd.map((tpl, i) => ({
        id:         `${Date.now()}${i}`,
        templateId: tpl.id,
        emoji:      tpl.emoji,
        label:      tpl.label,
        desc:       tpl.desc ?? '',
        days:       tpl.days,
        category:   tpl.category,
        startDate:  todayISO(),
        checkIns:   [],
        completed:  false,
        createdAt:  nowIso,
        updatedAt:  nowIso,
      }));
      return [...started, ...list];
    });
  }, [allReady, completedReady, challengesReady, levelReady, effectiveLevel, levelTasks, completedList, setChallenges]);

  return {
    syncChallengeProgress,
    forceSync,
    getChallengeProgress,
  };
}